import { CLIENT_INTERPOLATION_BUFFER_MS } from '../../shared/constants';
import type { Vec3 } from '../../shared/utils';
import { vec3Lerp } from '../../shared/utils';

/**
 * Shortest-arc lerp for angles in radians. Keeps drones from spinning a full
 * circle whenever heading wraps across ±π between snapshots.
 */
export function lerpAngle(a: number, b: number, t: number): number {
    let d = ((b - a + Math.PI) % (2 * Math.PI)) - Math.PI;
    if (d < -Math.PI) d += 2 * Math.PI;
    return a + d * t;
}

export interface InterpolatedDrone {
    id: string;
    position: Vec3;
    heading: number;
    pitch: number;
    health: number;
    maxHealth: number;
    alive: boolean;
    droneType: number;
    tier: number;
    ownerId: string;
    abilityCooldown: number;
    activeCardSlot: number;
    activeCardType: number;
    shielded: boolean;
    taunted: boolean;
    slowed: boolean;
    invulnerableMs: number;
    knockbackUntilMs: number;
    stunnedRemainingMs: number;
    dotUntilMs: number;
    dotDps: number;
}

/**
 * Project a raw Colyseus drone schema into a plain {@link InterpolatedDrone}.
 *
 * Used by both `Interpolator.pushSnapshot()` (for buffered interpolation) and
 * `GameClient.extractDronesFromState()` (the no-snapshot fallback path). Keeping
 * the projection in one place ensures status fields can never silently drift
 * between the two render paths.
 */
export function rawDroneToInterpolated(drone: any): InterpolatedDrone {
    return {
        id: drone.id,
        position: {
            x: drone.position.x,
            y: drone.position.y,
            z: drone.position.z,
        },
        heading: drone.heading,
        pitch: drone.pitch,
        health: drone.health,
        maxHealth: drone.maxHealth,
        alive: drone.alive,
        droneType: drone.droneType,
        tier: drone.tier,
        ownerId: drone.ownerId,
        abilityCooldown: drone.abilityCooldown ?? 0,
        activeCardSlot: drone.activeCardSlot ?? 255,
        activeCardType: drone.activeCardType ?? 255,
        shielded: drone.shielded === true,
        taunted: drone.taunted === true,
        slowed: drone.slowed === true,
        invulnerableMs: drone.invulnerableMs ?? 0,
        knockbackUntilMs: drone.knockbackUntilMs ?? 0,
        stunnedRemainingMs: drone.stunnedRemainingMs ?? 0,
        dotUntilMs: drone.dotUntilMs ?? 0,
        dotDps: drone.dotDps ?? 0,
    };
}

interface Snapshot {
    timestamp: number;
    drones: Map<string, InterpolatedDrone>;
}

export class Interpolator {
    private snapshots: Snapshot[] = [];
    private bufferMs: number;
    private maxSnapshots = 10;
    private lastKnownPositions = new Map<string, Vec3>();

    constructor(bufferMs: number = CLIENT_INTERPOLATION_BUFFER_MS) {
        this.bufferMs = bufferMs;
    }

    pushSnapshot(state: any, serverTime: number): void {
        const drones = new Map<string, InterpolatedDrone>();

        if (state.players) {
            state.players.forEach((player: any) => {
                if (player.drones) {
                    player.drones.forEach((drone: any) => {
                        drones.set(drone.id, rawDroneToInterpolated(drone));
                    });
                }
            });
        }

        this.snapshots.push({ timestamp: serverTime, drones });
        if (this.snapshots.length > this.maxSnapshots) {
            this.snapshots.shift();
        }
    }

    getInterpolatedDrones(clientTime: number): InterpolatedDrone[] {
        if (this.snapshots.length === 0) return [];
        if (this.snapshots.length === 1) {
            return Array.from(this.snapshots[0].drones.values());
        }

        const renderTime = clientTime - this.bufferMs;

        let older: Snapshot | null = null;
        let newer: Snapshot | null = null;
        for (let i = 0; i < this.snapshots.length - 1; i++) {
            if (
                this.snapshots[i].timestamp <= renderTime &&
                this.snapshots[i + 1].timestamp >= renderTime
            ) {
                older = this.snapshots[i];
                newer = this.snapshots[i + 1];
                break;
            }
        }

        if (!older || !newer) {
            return Array.from(this.snapshots[this.snapshots.length - 1].drones.values());
        }

        const range = newer.timestamp - older.timestamp;
        const t = range > 0 ? (renderTime - older.timestamp) / range : 0;
        const clampedT = Math.max(0, Math.min(1, t));

        // `let`-bound `older`/`newer` lose null-narrowing inside the forEach
        // closures below; const aliases preserve it without the unsafe `!`
        // non-null assertions (which were a latent crash if a future refactor
        // reached this block before the buffer had two snapshots).
        const olderSnap = older;
        const newerSnap = newer;

        const result: InterpolatedDrone[] = [];
        newerSnap.drones.forEach((newDrone, id) => {
            const oldDrone = olderSnap.drones.get(id);
            // Respawn transition (was dead, now alive): teleport to the new
            // position instead of lerping from the death point. Without this
            // the drone visibly slides across the arena over the buffer
            // window from where it died to where it respawned.
            if (oldDrone && oldDrone.alive === false && newDrone.alive === true) {
                this.lastKnownPositions.set(id, newDrone.position);
                result.push({ ...newDrone });
                return;
            }
            if (oldDrone) {
                const pos = vec3Lerp(oldDrone.position, newDrone.position, clampedT);
                this.lastKnownPositions.set(id, pos);
                result.push({
                    ...newDrone,
                    position: pos,
                    heading: lerpAngle(oldDrone.heading, newDrone.heading, clampedT),
                    pitch: lerpAngle(oldDrone.pitch, newDrone.pitch, clampedT),
                });
            } else {
                // New drone: lerp from last known position (e.g. spawn near parent) to smooth entry
                const lastPos = this.lastKnownPositions.get(id);
                if (lastPos) {
                    const pos = vec3Lerp(lastPos, newDrone.position, clampedT);
                    this.lastKnownPositions.set(id, pos);
                    result.push({ ...newDrone, position: pos });
                } else {
                    this.lastKnownPositions.set(id, newDrone.position);
                    result.push(newDrone);
                }
            }
        });

        // Clean up stale entries
        this.lastKnownPositions.forEach((_, id) => {
            if (!newerSnap.drones.has(id)) this.lastKnownPositions.delete(id);
        });

        return result;
    }
}
